import { useMemo, useState } from 'react';
import { IconButton } from '@portal_components/components/IconButton';
import { EExceptionType, EMpanAssignmentMode, MeterPointDetails } from '../../types';
import { MeterPointDetailsForm } from '../MeterPointDetailsForm/MeterPointDetailsForm';
import axios from 'axios';
import { EnvironmentVariables } from '../../EnvironmentVariables';
import { Modal } from '@portal_components/components/Modal';
import { EUserRole } from '@portal_components/types';
import { useUserInfo } from '../../hooks/useUserInfo';

interface HandoverExceptionsListItemProps {
  siteDetails: MeterPointDetails;
  index: number;
  expandedRecordIndex: number | null;
  appointmentActive: boolean;
  errorDescription?: string;
  detailsInOds?: boolean;
  appointmentDuplicate?: boolean;
  contractId: string;
  customerGroupId: string;
  handoverFileName: string;
  setExpandedRecordIndex: (index: number | null) => void;
}

export const HandoverExceptionsListItem = ({
  siteDetails,
  index,
  expandedRecordIndex,
  appointmentActive,
  appointmentDuplicate,
  errorDescription,
  detailsInOds,
  customerGroupId,
  contractId,
  handoverFileName,
  setExpandedRecordIndex
}: HandoverExceptionsListItemProps) => {
  const { userInfo } = useUserInfo();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [resolved, setResolved] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleExpandClicked = () => {
    setExpandedRecordIndex(index !== expandedRecordIndex ? index : null);
  };
  const [deleteProcessing, setDeleteProcessing] = useState<boolean>(false);

  const handleDeleteClicked = () => {
    if (resolved || deleted) return;
    setShowDeleteConfirmation(true);
  };

  const borderColour = useMemo(() => {
    if (resolved) return 'border-feedback-green';
    if (deleted) return 'border-feedback-red';
    if (!appointmentActive) return 'border-feedback-orange';
    return 'border-feedback-blue';
  }, [appointmentActive, deleted, resolved]);

  const formMode = useMemo(() => {
    if (resolved || deleted || userInfo?.role === EUserRole.READONLY_USER) {
      return EMpanAssignmentMode.VIEW;
    }
    if (detailsInOds) {
      return EMpanAssignmentMode.EDIT;
    }
    return EMpanAssignmentMode.NEW;
  }, [deleted, detailsInOds, resolved, userInfo?.role]);

  const handleDelete = async (): Promise<boolean> => {
    setDeleteProcessing(true);
    const response = await axios
      .delete(`${EnvironmentVariables.ApiUrl}/exceptions?handoverFileName=${encodeURIComponent(handoverFileName)}&exceptionType=${EExceptionType.VALIDATION_EXCEPTION}`, {
        data: { mpans: [siteDetails.mprn] },
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      })
      .catch(error => error.response);
    setDeleteProcessing(false);
    if (!response || response.status !== 200) {
      return false;
    }
    return true;
  };

  const onDelete = async () => {
    if (deleteProcessing || userInfo?.role === EUserRole.READONLY_USER) return;
    const successful = await handleDelete();
    if (!successful) {
      setErrorMessage('Failed to delete exception');
      return;
    }
    setShowDeleteConfirmation(false);
    setDeleted(true);
    setExpandedRecordIndex(null);
  };

  const onSubmit = async () => {
    if (deleteProcessing || userInfo?.role === EUserRole.READONLY_USER) return;
    const successful = await handleDelete();
    if (!successful) {
      setErrorMessage('Site details were created but exception failed to delete.');
      return;
    }
    setExpandedRecordIndex(null);
    setResolved(true);
  };

  return (
    <>
      <div key={siteDetails.mprn} className="pb-4">
        <div className={`flex flex-row justify-between mb-2 gap-1 py-2 px-4 rounded-sm min-h-20 bg-elevation-1 border-solid border-l-[5px] ${borderColour} relative`}>
          <div className="flex flex-col">
            <p className="text-lg">MPAN: {siteDetails.mprn}</p>
            {!resolved && !deleted && !errorMessage && (
              <>
                {!appointmentActive && <p className="text-feedback-orange">No Active Appointment</p>}
                {appointmentDuplicate && <p className="text-feedback-orange">Duplicate MPAN. This MPAN is already assigned to the selected contract</p>}
                {errorDescription && <p className="text-lg text-feedback-red">{`There are errors related to this site. Expand for additional details: ${errorDescription}`}</p>}
              </>
            )}
            {errorMessage && <p className="text-feedback-red">{errorMessage}</p>}
            {resolved && <p className="text-feedback-green">Exception Resolved</p>}
            {deleted && <p className="text-feedback-red">Exception Deleted</p>}
          </div>
          <div className="flex flex-row gap-4 pt-3 shrink-0">
            {!resolved && !deleted && userInfo && userInfo.role !== EUserRole.READONLY_USER && <IconButton icon="/delete.svg" onClick={handleDeleteClicked} />}
            <div className="pt-3">
              {expandedRecordIndex === index ? <IconButton icon="/arrowUp.svg" onClick={handleExpandClicked} /> : <IconButton icon="/arrowDown.svg" onClick={handleExpandClicked} />}
            </div>
          </div>
        </div>
        {expandedRecordIndex === index && siteDetails.mprn && (
          <div className="pb-4">
            <MeterPointDetailsForm
              mpan={siteDetails.mprn}
              customerGroupId={customerGroupId}
              contractId={contractId}
              existingMeterPointDetails={siteDetails}
              mode={formMode}
              onSubmit={onSubmit}
              handoverFileName={handoverFileName}
            />
          </div>
        )}
      </div>
      {showDeleteConfirmation && (
        <Modal
          primaryMessage={`Are you sure you want to delete this exception for MPAN: ${siteDetails.mprn}`}
          secondaryMessage="This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          onConfirm={onDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          primaryDisabled={deleteProcessing}
        />
      )}
    </>
  );
};
